/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : RowColumnDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import {
  Box,
  IconButton,
  Modal,
  Paper,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import PropTypes from "prop-types";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch } from "react-redux";
import { HandleApiActions, legalRequirements } from "../../redux/actions";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../../components/customComponentsV2/common/select/CustomSelectV2";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";

function RowColumnDialog({
  openRowColumn,
  setOpenRowColumn,
  selectedLSTableDetails,
  selectedLSTable,
  selectedLSDetails,
  setSelectedLSTableDetails,
  setSubmitIconDisable,
}) {
  const dispatch = useDispatch();
  const [columns, setColumns] = useState({
    column_key: 0,
    column_label: "",
    column_type: "",
  });
  const handleCloseDialog = () => {
    setOpenRowColumn(false);
  };
  const handleSubmit = async () => {
    const reqObj = {
      column_key: 1,
      column_label: columns.label,
      column_type: columns.type,
      table_id: selectedLSTableDetails?.table_id,
    };
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...legalRequirements.add_legal_standard_table_column,
          params: reqObj,
          show_toast: true,
        }),
      ),
    );
    if (!resp.error) {
      fetchTableDetails();
      handleCloseDialog();
    }
  };
  const fetchTableDetails = async () => {
    const resp = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...legalRequirements.get_legal_standard_table_details,

          params: {
            ls_id: selectedLSDetails,
            table_id: selectedLSTable,
          },
        }),
      ),
    );
    setSelectedLSTableDetails(resp?.data?.[0]?.legal_standard_table);
  };
  return (
    <div>
      <Modal open={openRowColumn} onClose={handleCloseDialog}>
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
            p: 2,
            borderRadius: 1,
          }}
        >
          <Paper
            elevation={24}
            sx={{
              borderRadius: 3,
              width: "100%",
              maxWidth: "300px",
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <Typography>Add Column</Typography>
              <IconButton
                onClick={handleCloseDialog}
                sx={{ "&:hover": { color: "#E5E7EB" } }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
            <Box>
              <Box sx={{ pl: 2, pr: 2, mt: 1 }}>
                <CustomFormTextFieldV2
                  label="Column Label"
                  required={true}
                  value={columns.label || ""}
                  setValue={(val) =>
                    setColumns((prev) => ({ ...prev, label: val }))
                  }
                  placeholder="Enter Column Label"
                />
              </Box>
              <Box sx={{ pl: 2, pr: 2, mt: 1, mb: 2 }}>
                <CustomSelectV2
                  label="Column Type"
                  enableSearch={false}
                  required={true}
                  handleOnChange={(e) =>
                    setColumns((prev) => ({ ...prev, type: e.target.value }))
                  }
                  selectedValue={columns.type || ""}
                  optionsList={[
                    { label: "string", value: "string" },
                    { label: "icon", value: "icon" },
                  ]}
                  sltFldStyle={{
                    width: "270px",
                    fontSize: "0.95rem",
                    borderRadius: 0,
                  }}
                />
              </Box>
            </Box>
            <Box
              sx={{
                bgcolor: "#FFF",
                px: 4,
                py: 2,
                display: "flex",
                justifyContent: "flex-end",
                borderTop: "1px solid #E5E7EB",
              }}
            >
              <CustomButtonV2
                type={"submit"}
                btnName="Add"
                variant="contained"
                handleOnClick={handleSubmit}
              >
                Add
              </CustomButtonV2>
            </Box>
          </Paper>
        </Box>
      </Modal>
    </div>
  );
}

RowColumnDialog.propTypes = {
  openRowColumn: PropTypes.bool,
  setOpenRowColumn: PropTypes.func,
  selectedLSTableDetails: PropTypes.shape({
    table_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  selectedLSTable: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  selectedLSDetails: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]),
  setSelectedLSTableDetails: PropTypes.func,
  setSubmitIconDisable: PropTypes.func,
};

export default RowColumnDialog;
